/**
 * MarketSelector Component
 * Prop type selection toggles
 */

import { cn } from '@utils/cn';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type React from 'react';
import { useCallback, useMemo, useState } from 'react';
import type { MarketOption } from '../constants';

// =============================================================================
// Types
// =============================================================================

interface MarketSelectorProps {
  markets: MarketOption[];
  selectedMarkets: string[];
  onToggleMarket: (marketKey: string) => void;
}

// Category labels and order for rebrand grouped view
const CATEGORY_CONFIG: Record<string, { label: string; order: number }> = {
  game: { label: 'Game Props', order: 0 },
  team: { label: 'Team Props', order: 1 },
  basic: { label: 'Player Props', order: 2 },
  shooting: { label: 'Shooting', order: 3 },
  combo: { label: 'Combo Props', order: 4 },
  special: { label: 'Special', order: 5 },
  alt_player: { label: 'Alt Player Props', order: 6 },
  half_game: { label: 'Half — Game', order: 7 },
  half_team: { label: 'Half — Team', order: 8 },
  quarter_game: { label: 'Quarter — Game', order: 9 },
  quarter_team: { label: 'Quarter — Team', order: 10 },
  quarter_player: { label: 'Quarter — Player', order: 11 },
};

// Categories expanded by default (first 6)
const DEFAULT_EXPANDED_CATEGORIES = new Set([
  'game',
  'team',
  'basic',
  'shooting',
  'combo',
  'special',
]);

// =============================================================================
// Component
// =============================================================================

export function MarketSelector({
  markets,
  selectedMarkets,
  onToggleMarket,
}: MarketSelectorProps): React.ReactElement {
  const [isExpanded, setIsExpanded] = useState(
    () => typeof window === 'undefined' || !window.matchMedia('(max-width: 639px)').matches,
  );
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(() => {
    const collapsed = new Set<string>();
    for (const key of Object.keys(CATEGORY_CONFIG)) {
      if (!DEFAULT_EXPANDED_CATEGORIES.has(key)) collapsed.add(key);
    }
    return collapsed;
  });
  const selectedLabels = markets.filter((m) => selectedMarkets.includes(m.key)).map((m) => m.label);

  const toggleCategory = useCallback((category: string) => {
    setCollapsedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return next;
    });
  }, []);

  // Group markets by category
  const groupedMarkets = useMemo(() => {
    const groups = new Map<string, MarketOption[]>();
    for (const market of markets) {
      const cat = market.category || 'basic';
      const existing = groups.get(cat) || [];
      existing.push(market);
      groups.set(cat, existing);
    }
    return Array.from(groups.entries()).sort(
      ([a], [b]) => (CATEGORY_CONFIG[a]?.order ?? 99) - (CATEGORY_CONFIG[b]?.order ?? 99),
    );
  }, [markets]);

  const handleSelectCategory = (category: string): void => {
    const categoryMarkets = markets.filter((m) => m.category === category);
    const allSelected = categoryMarkets.every((m) => selectedMarkets.includes(m.key));
    for (const m of categoryMarkets) {
      if (allSelected) {
        if (selectedMarkets.includes(m.key)) onToggleMarket(m.key);
      } else {
        if (!selectedMarkets.includes(m.key)) onToggleMarket(m.key);
      }
    }
  };

  return (
    <div className="mb-4" data-testid="market-selector">
      {/* Header row */}
      <div className="flex items-center justify-between gap-2 mb-2">
        {/* Header - tappable on mobile to expand/collapse */}
        <div
          onClick={() => setIsExpanded((prev) => !prev)}
          className="flex items-center gap-2 cursor-pointer min-w-0 flex-1"
        >
          <ChevronDown
            className={cn('w-4 h-4 shrink-0 transition-transform', !isExpanded && '-rotate-90')}
          />
          <h4 className="text-sm font-medium text-theme-text-secondary shrink-0">PROP TYPES</h4>
          {/* Collapsed summary */}
          {!isExpanded && (
            <div className="flex items-center gap-2 min-w-0 flex-1 overflow-hidden">
              <span className="text-xs text-theme-text-tertiary shrink-0">
                {selectedMarkets.length} selected:
              </span>
              <div className="flex gap-1 overflow-hidden">
                {selectedLabels.slice(0, 3).map((label) => (
                  <span
                    key={label}
                    className="text-xs px-1.5 py-0.5 rounded-full whitespace-nowrap shrink-0 bg-theme-primary/30 text-theme-primary"
                  >
                    {label}
                  </span>
                ))}
                {selectedLabels.length > 3 && (
                  <span className="text-xs text-theme-text-tertiary whitespace-nowrap shrink-0">
                    +{selectedLabels.length - 3} more
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Grouped by category */}
      {isExpanded && groupedMarkets && (
        <div className="space-y-3">
          {groupedMarkets.map(([category, categoryMarkets]) => {
            const config = CATEGORY_CONFIG[category];
            const allSelected = categoryMarkets.every((m) => selectedMarkets.includes(m.key));
            const isCategoryCollapsed = collapsedCategories.has(category);
            const selectedCount = categoryMarkets.filter((m) =>
              selectedMarkets.includes(m.key),
            ).length;
            return (
              <div key={category}>
                <div className="flex items-center gap-2 mb-1.5">
                  <button
                    type="button"
                    onClick={() => toggleCategory(category)}
                    className="flex items-center gap-1 group"
                  >
                    {isCategoryCollapsed ? (
                      <ChevronRight className="w-3 h-3 text-theme-text-tertiary group-hover:text-theme-text-secondary transition" />
                    ) : (
                      <ChevronDown className="w-3 h-3 text-theme-text-tertiary group-hover:text-theme-text-secondary transition" />
                    )}
                    <span className="text-[10px] font-semibold text-theme-text-tertiary uppercase tracking-wider group-hover:text-theme-text-secondary transition">
                      {config?.label || category}
                    </span>
                  </button>
                  {isCategoryCollapsed && selectedCount > 0 && (
                    <span className="text-[10px] text-theme-primary">
                      {selectedCount}/{categoryMarkets.length}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => handleSelectCategory(category)}
                    className="text-[10px] text-theme-primary hover:text-theme-primary-hover transition"
                  >
                    {allSelected ? 'Clear' : 'All'}
                  </button>
                  <div className="flex-1 h-px bg-theme-border/30" />
                </div>
                {!isCategoryCollapsed && (
                  <div className="flex flex-wrap gap-1.5">
                    {categoryMarkets.map((market) => (
                      <button
                        type="button"
                        key={market.key}
                        onClick={() => onToggleMarket(market.key)}
                        className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all ${
                          selectedMarkets.includes(market.key)
                            ? 'bg-theme-primary-fill text-theme-on-primary-fill shadow-sm'
                            : 'bg-theme-surface-hover text-theme-text-secondary hover:bg-theme-surface-active'
                        }`}
                      >
                        {market.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
